import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const REVERT_STATUS_OPTIONS = [
  { key: 'new',       label: 'New Lead' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'booked_in', label: 'Booked In' },
  { key: 'quoted',    label: 'Quoted' },
  { key: 'lost',      label: 'Lost' },
  { key: 'archived',  label: 'Archived' },
];

/**
 * Two-step modal for reverting a "Won" lead back to a funnel stage. Step 1 confirms
 * the destructive action (deletes the linked project). Step 2 picks the destination
 * status. Submit calls `onConfirm(chosenStatus)`.
 */
export default function RevertToLeadModal({
  lead,
  step,
  chosenStatus,
  onPickStatus,
  reverting,
  onClose,
  onAdvanceToPick,
  onConfirm,
  fmtCurrency,
}) {
  if (!lead) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="revert-lead-modal"
    >
      <div
        className="bg-white rounded-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-100 flex items-center gap-2">
          <ArrowLeft className="w-5 h-5 text-emerald-700" />
          <h3 className="text-base font-bold text-gray-900">
            {step === 'confirm' ? 'Convert back to lead?' : 'Choose a lead stage'}
          </h3>
        </div>

        {step === 'confirm' && (
          <div className="p-4 space-y-3">
            <p className="text-sm text-gray-700 leading-relaxed">
              This will <strong>delete the project</strong> from your project management section.
              Are you sure you want to convert <strong>{lead.customer_name}</strong> back into a lead?
            </p>
            {lead.project_reference && (
              <div className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded p-2">
                Project to be deleted: <strong>{lead.project_reference}</strong>
                {fmtCurrency(lead.project_value) ? ` · ${fmtCurrency(lead.project_value)}` : ''}
              </div>
            )}
          </div>
        )}

        {step === 'pick-status' && (
          <div className="p-4 space-y-3">
            <p className="text-sm text-gray-700">
              What stage would you like <strong>{lead.customer_name}</strong>&apos;s lead to go into?
            </p>
            <div className="grid grid-cols-2 gap-2">
              {REVERT_STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  data-testid={`revert-status-${opt.key}`}
                  onClick={() => onPickStatus(opt.key)}
                  className={`px-3 py-2 rounded border text-sm font-semibold text-left transition-colors ${
                    chosenStatus === opt.key
                      ? 'bg-emerald-600 border-emerald-600 text-white'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-emerald-300 hover:bg-emerald-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={reverting}
            data-testid="revert-cancel"
          >
            No
          </Button>
          {step === 'confirm' ? (
            <Button
              size="sm"
              onClick={onAdvanceToPick}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="revert-confirm-yes"
            >
              Yes
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={onConfirm}
              disabled={!chosenStatus || reverting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="revert-confirm-final"
            >
              {reverting ? 'Reverting…' : 'Convert back'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
